/**
 * Facility Name autocomplete — inline table-cell combobox backed by the
 * global facility catalog (`/v2/facilities/?search=`).
 *
 * Behaviour (Henry/Gustavo, jun-19):
 *   • The cell is a free-text input. Typing fires a debounced catalog
 *     search (by CMS number OR name) and shows the matches in a dropdown.
 *   • Picking a result auto-fills the row (name, CMS/provider number,
 *     address, beds…) via `onSelect`.
 *   • Not picking anything keeps whatever the user typed — they can enter
 *     a brand-new facility or any free text.
 *
 * Why a hand-rolled dropdown instead of atlas `SearchSelect` / a react-aria
 * ComboBox: the Schedule table wraps its cells in `EditableCellProvider`,
 * which swallows keydown events on editable inputs (so the Table grid's
 * arrow/space nav doesn't fight typing). That also kills a combobox's own
 * keyboard nav, so selection here is pointer-driven. The result list is
 * portalled to <body> so the table's `overflow:auto` never clips it.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { cx } from "@tricura/atlas-ui";
import { searchFacilityCatalog, type CatalogFacility } from "../api/endpoints/facility-catalog";
import { useDebouncedValue } from "../hooks/use-debounced-value";

// Matches the editable-cell.tsx shell so the field is visually identical to
// the other Facility Schedule cells. `min-h-9` (not fixed h-9) so the display
// mode can grow when the name wraps to multiple lines (Andressa, jun-22).
const CELL_SHELL =
    "relative flex min-h-9 w-full rounded-lg border px-3 text-sm transition-colors hover:bg-secondary_hover focus-within:border-brand-primary";
const CELL_INPUT = "h-9 w-full bg-transparent text-primary outline-none placeholder:text-placeholder";
// Display mode: the full name wraps onto multiple lines within the 240px cell.
// Capped at ~3 lines — longer names get a small vertical scrollbar instead of
// growing the row unbounded (Andressa, jun-22).
const CELL_DISPLAY =
    "w-full max-h-20 overflow-y-auto whitespace-normal [overflow-wrap:anywhere] py-2 text-left leading-snug text-primary outline-none";

/** Don't hit the API for trivially short terms. */
const MIN_QUERY = 2;

/** "715 N Medford Rd, Brownsville, TX" — the catalog row's address line,
 *  shown under the name in the dropdown as a visual differentiator. */
function catalogAddress(f: CatalogFacility): string {
    return [f.address, f.city, f.state].map((s) => s?.trim()).filter(Boolean).join(", ");
}

interface FacilityNameAutocompleteProps {
    value: string;
    /** Free-text edits (every keystroke). */
    onChange: (name: string) => void;
    /** A catalog facility was picked — caller auto-fills the row. */
    onSelect: (facility: CatalogFacility) => void;
    placeholder?: string;
    ariaLabel: string;
    /** Red error border (paired with an InvalidShell tooltip by the caller). */
    isInvalid?: boolean;
    /** Custom attribute for E2E testing. */
    "data-autotest"?: string;
}

export function FacilityNameAutocomplete({ value, onChange, onSelect, placeholder, ariaLabel, isInvalid, "data-autotest": dataAutotest }: FacilityNameAutocompleteProps) {
    const inputRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLDivElement>(null);
    const [open, setOpen] = useState(false);
    /** Edit vs display: while editing it's a single-line input that scrolls
     *  horizontally; when not editing (and there's a value) the name wraps
     *  to multiple lines (Andressa, jun-22). */
    const [editing, setEditing] = useState(false);
    const [results, setResults] = useState<CatalogFacility[]>([]);
    const [loading, setLoading] = useState(false);
    const [rect, setRect] = useState<DOMRect | null>(null);
    /** Set right after a pick so the debounced effect doesn't immediately
     *  re-search the value we just auto-filled (and reopen the dropdown). */
    const skipNextSearch = useRef(false);

    // Local buffer so typing paints instantly, decoupled from the deferred
    // SoT round-trip + derived recalc the change triggers. Re-syncs only on
    // genuine external changes (pick auto-fill, save, sibling) — never on the
    // lagging echo of our own emit, so characters never revert.
    const lastEmitted = useRef(value);
    const [text, setText] = useState(value);
    useEffect(() => {
        if (value !== lastEmitted.current) {
            lastEmitted.current = value;
            setText(value);
        }
    }, [value]);

    const debounced = useDebouncedValue(text, 300);

    // Debounced catalog search. Guards against out-of-order responses by
    // pinning each request to its query string.
    useEffect(() => {
        if (skipNextSearch.current) {
            skipNextSearch.current = false;
            return;
        }
        const q = debounced.trim();
        if (q.length < MIN_QUERY) {
            setResults([]);
            setLoading(false);
            return;
        }
        let cancelled = false;
        setLoading(true);
        searchFacilityCatalog(q, 8)
            .then((rows) => {
                if (cancelled) return;
                setResults(rows);
                setLoading(false);
            })
            .catch(() => {
                if (cancelled) return;
                setResults([]);
                setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [debounced]);

    // Track the input's viewport rect while the dropdown is open so the
    // portalled list stays anchored through scroll / resize.
    useEffect(() => {
        if (!open) return;
        const updateRect = () => {
            if (inputRef.current) setRect(inputRef.current.getBoundingClientRect());
        };
        updateRect();
        window.addEventListener("scroll", updateRect, true);
        window.addEventListener("resize", updateRect);
        return () => {
            window.removeEventListener("scroll", updateRect, true);
            window.removeEventListener("resize", updateRect);
        };
    }, [open]);

    // Close on outside pointer-down (the list lives in a portal, so a plain
    // blur can't tell "clicked an item" from "clicked away").
    useEffect(() => {
        if (!open) return;
        const onPointerDown = (e: PointerEvent) => {
            const t = e.target as Node;
            if (inputRef.current?.contains(t) || listRef.current?.contains(t)) return;
            setOpen(false);
        };
        document.addEventListener("pointerdown", onPointerDown, true);
        return () => document.removeEventListener("pointerdown", onPointerDown, true);
    }, [open]);

    const handleSelect = useCallback(
        (facility: CatalogFacility) => {
            skipNextSearch.current = true;
            lastEmitted.current = facility.name;
            setText(facility.name);
            onSelect(facility);
            setOpen(false);
            setResults([]);
            // Picking confirms the value → drop to the wrapped display mode.
            setEditing(false);
            inputRef.current?.blur();
        },
        [onSelect],
    );

    const showList = open && rect && (loading || results.length > 0);
    // Input while editing (or when empty so the placeholder shows); otherwise
    // the wrapped, read-only display of the confirmed name.
    const showInput = editing || text.trim() === "";

    return (
        <div className={cx(CELL_SHELL, showInput ? "items-center" : "items-start", isInvalid ? "border-error" : "border-transparent")}>
            {showInput ? (
                <input
                    ref={inputRef}
                    type="text"
                    data-autotest={dataAutotest}
                    aria-label={ariaLabel}
                    value={text}
                    placeholder={placeholder}
                    className={CELL_INPUT}
                    autoComplete="off"
                    autoFocus={editing}
                    onFocus={() => {
                        setEditing(true);
                        setOpen(true);
                    }}
                    onBlur={() => setEditing(false)}
                    onChange={(e) => {
                        lastEmitted.current = e.target.value;
                        setText(e.target.value);
                        onChange(e.target.value);
                        setOpen(true);
                    }}
                />
            ) : (
                <button type="button" data-autotest={dataAutotest} aria-label={ariaLabel} className={CELL_DISPLAY} onClick={() => setEditing(true)}>
                    {text}
                </button>
            )}

            {showList &&
                createPortal(
                    <div
                        ref={listRef}
                        style={{
                            position: "fixed",
                            top: rect.bottom + 4,
                            left: rect.left,
                            width: Math.max(rect.width, 280),
                            zIndex: 1000,
                        }}
                        className="ring-secondary_alt max-h-72 overflow-y-auto rounded-lg bg-primary py-1 shadow-lg ring-1"
                    >
                        {loading && results.length === 0 ? (
                            <div className="text-tertiary px-3 py-2 text-sm">Searching…</div>
                        ) : (
                            results.map((f) => (
                                <button
                                    key={f.id}
                                    type="button"
                                    // preventDefault on mousedown keeps the input focused so
                                    // the click lands before any blur teardown.
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => handleSelect(f)}
                                    className="hover:bg-primary_hover flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left"
                                >
                                    <span className="text-primary text-sm font-medium break-words">{f.name}</span>
                                    {/* Address line — gray, helps the user tell facilities apart. */}
                                    {catalogAddress(f) && (
                                        <span className="text-tertiary text-xs break-words">{catalogAddress(f)}</span>
                                    )}
                                    {f.cms_number && <span className="text-quaternary text-xs">CMS {f.cms_number}</span>}
                                </button>
                            ))
                        )}
                    </div>,
                    document.body,
                )}
        </div>
    );
}
